import { Layers3, ChevronDown, ChevronUp } from "lucide-react"
import { useMemo } from "react"
import { useStore } from "zustand"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  useTalentConfigRouteStore,
  setIsHierarchyOpen,
  setOpenTracks,
} from "@/lib/stores/talent-config-ui-store"
import {
  type TalentConfigBoss,
  type TalentCategory,
  type TalentConfigTalent,
} from "@/lib/talent-config-api"
import { isTalentDirty } from "@/lib/utils/talent-draft.utils"

const CATEGORY_LABELS: Record<TalentCategory, string> = {
  offensive: "Offensive",
  defensive: "Defensive",
  strategic: "Strategic",
}

const CATEGORY_TIER_BASE: Record<TalentCategory, number> = {
  offensive: 1,
  defensive: 4,
  strategic: 7,
}

const CATEGORY_TIER_LIMIT: Record<TalentCategory, number> = {
  offensive: 3,
  defensive: 3,
  strategic: 3,
}

const TRACK_ORDER: TalentCategory[] = ["offensive", "defensive", "strategic"]

type TrackTier = {
  tier: number
  talentId: string | null
  isFilled: boolean
  isDirty: boolean
}

export function HierarchyEditor({ boss }: { boss: TalentConfigBoss }) {
  const store = useTalentConfigRouteStore()
  const isHierarchyOpen = useStore(store, (s) => s.isHierarchyOpen)
  const talentDrafts = useStore(store, (s) => s.talentDrafts)
  const openTracks = useStore(store, (s) => s.openTracks)

  const trackTree = useMemo(() => {
    return TRACK_ORDER.map((category) => {
      const start = CATEGORY_TIER_BASE[category]
      const end = start + CATEGORY_TIER_LIMIT[category] - 1
      const talents = boss.talentsByCategory[category]
      const byTier = new Map(talents.map((talent) => [talent.tier, talent]))

      const tiers = Array.from({ length: end - start + 1 }, (_, index) => {
        const tier = start + index
        const talent = byTier.get(tier) ?? null
        return {
          tier,
          talentId: talent?.id ?? null,
          isFilled: Boolean(talent),
          isDirty: talent
            ? isTalentDirty(talentDrafts[talent.id], talent)
            : false,
        }
      })

      return {
        category,
        label: CATEGORY_LABELS[category],
        filled: talents.length,
        total: CATEGORY_TIER_LIMIT[category],
        tiers,
      }
    })
  }, [boss, talentDrafts])

  const focusTrackSection = (category: TalentCategory) => {
    setOpenTracks(store, {
      ...openTracks,
      [category]: true,
    })

    const elementId = getTrackSectionId(category)
    if (!elementId) {
      return
    }

    requestAnimationFrame(() => {
      const section = document.getElementById(elementId)
      section?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  return (
    <Card className="gap-0">
      <Collapsible
        open={isHierarchyOpen}
        onOpenChange={(e) => setIsHierarchyOpen(store, e)}
      >
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardHeading icon={<Layers3 className="size-5 text-primary" />}>
                Talent Hierarchy
              </CardHeading>
              <CardDescription>
                Tree view of track tiers for this boss. Use jump to open and
                scroll to a track editor section.
              </CardDescription>
            </div>
            <CollapsibleTrigger className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground">
              {isHierarchyOpen ? "Collapse" : "Expand"}
              {isHierarchyOpen ? (
                <ChevronUp className="size-3.5" />
              ) : (
                <ChevronDown className="size-3.5" />
              )}
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="grid gap-3">
            {trackTree.map((track) => (
              <div
                key={track.category}
                className="rounded-[1.2rem] border border-border/60 bg-card/35 p-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {track.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {track.filled}/{track.total} tiers configured
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => focusTrackSection(track.category)}
                  >
                    Jump
                  </Button>
                </div>

                <div className="mt-3 grid gap-1.5">
                  {track.tiers.map((entry) => (
                    <div
                      key={`${track.category}-${entry.tier}`}
                      className="flex items-center justify-between rounded-lg border border-border/55 bg-background/70 px-2.5 py-1.5 text-xs"
                    >
                      <span className="font-medium text-foreground/90">
                        Tier {entry.tier}
                      </span>
                      {entry.isFilled ? (
                        <span
                          className={`rounded-full border px-2 py-0.5 font-semibold ${entry.isDirty ? "border-warning/35 bg-warning/12 text-warning" : "border-success/35 bg-success/10 text-success"}`}
                        >
                          {entry.isDirty ? "Unsaved" : "Configured"}
                        </span>
                      ) : (
                        <span className="rounded-full border border-border/60 bg-card px-2 py-0.5 font-semibold text-muted-foreground">
                          Empty
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

function getTrackSectionId(category: TalentCategory): string {
  return `talent-track-${category}`
}
